<template>
  <div class="products-carousel" ref="carouselRoot">
    <!-- Loading skeleton (shown while loading) -->
    <div v-if="isLoading" class="row">
      <div
        v-for="i in skeletonCount"
        :key="`skeleton-${i}`"
        :class="gridClass"
      >
        <div class="product-skeleton">
          <div class="skeleton-img"></div>
          <div class="skeleton-body">
            <div class="skeleton-brand"></div>
            <div class="skeleton-text"></div>
            <div class="skeleton-text short"></div>
            <div class="skeleton-price"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile/Tablet: Horizontal scrollable -->
    <div v-else-if="isMobile" class="mobile-scroll-container">
      <div class="horizontal-scroll" ref="horizontalScroll">
        <div
          v-for="(product, index) in props.products"
          :key="product.id"
          class="product-scroll-item product-reveal-item"
          :style="{ '--reveal-delay': `${cardRevealDelay(index)}ms` }"
        >
          <ProductCard :product="product" />
        </div>
      </div>
      
      <!-- Mobile arrows -->
      <div class="carousel-arrows" v-if="props.products.length > 1">
        <button
          type="button"
          class="arrow-btn"
          :disabled="activeMobileSlide === 0"
          aria-label="Ver producto anterior"
          @click="scrollToProduct(activeMobileSlide - 1)"
        >
          <i class="fas fa-chevron-left" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          class="arrow-btn"
          :disabled="activeMobileSlide >= props.products.length - 1"
          aria-label="Ver producto siguiente"
          @click="scrollToProduct(activeMobileSlide + 1)"
        >
          <i class="fas fa-chevron-right" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <!-- Desktop: Carousel when products are loaded -->
    <div v-else class="position-relative">
      <div class="carousel-viewport">
        <Transition
          :name="slideTransition"
        >
          <div
            :key="activeSlide"
            class="carousel-slide"
          >
            <div class="row g-4 d-flex justify-content-center">
              <div
                v-for="(product, index) in productGroups[activeSlide]"
                :key="`${activeSlide}-${index}-${product.id}`"
                :class="[gridClass, 'product-reveal-item']"
                :style="{ '--reveal-delay': `${cardRevealDelay(index)}ms` }"
              >
                <ProductCard :product="product" />
              </div>
            </div>
          </div>
        </Transition>
      </div>

      <!-- Desktop arrows -->
      <div class="carousel-arrows" v-if="productGroups.length > 1">
        <button
          type="button"
          class="arrow-btn"
          aria-label="Ver productos anteriores"
          @click="prevSlide"
        >
          <i class="fas fa-chevron-left" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          class="arrow-btn"
          aria-label="Ver productos siguientes"
          @click="nextSlide"
        >
          <i class="fas fa-chevron-right" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, defineProps } from 'vue';
import ProductCard from '@/components/ProductCard.vue';

const props = defineProps({
  products: {
    type: Array,
    required: true
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  itemsPerSlide: {
    type: Number,
    default: 3
  }
});

// Track active slide index
const activeSlide = ref(0);
const activeMobileSlide = ref(0);
const carouselRoot = ref(null);

const slideTransition = computed(() => 'slide-fade');

const gridClass = computed(() => {
  switch (props.itemsPerSlide) {
    case 4:
      return 'col-6 col-md-3';
    case 2:
      return 'col-6 col-md-6';
    case 1:
      return 'col-12';
    default:
      return 'col-6 col-md-4';
  }
});

const skeletonCount = computed(() => Math.max(1, props.itemsPerSlide));

const cardRevealDelay = (index) => 90 + index * 75;

// Track if device is mobile/tablet
const isMobile = ref(window.innerWidth < 1024);

// Reference to horizontal scroll element
const horizontalScroll = ref(null);

// Group products into slides (for desktop)
const productGroups = computed(() => {
  const groups = [];
  const productsArr = Array.isArray(props.products) ? props.products : [];
  const itemsPerRow = Math.max(1, Number(props.itemsPerSlide) || 1);
  
  for (let i = 0; i < productsArr.length; i += itemsPerRow) {
    groups.push(productsArr.slice(i, i + itemsPerRow));
  }

  const lastGroup = groups[groups.length - 1];
  if (groups.length > 1 && lastGroup.length > 0 && lastGroup.length < itemsPerRow) {
    const productsToReuse = productsArr.slice(0, productsArr.length - lastGroup.length);
    let reuseIndex = 0;

    while (lastGroup.length < itemsPerRow && productsToReuse.length > 0) {
      lastGroup.push(productsToReuse[reuseIndex % productsToReuse.length]);
      reuseIndex += 1;
    }
  }

  return groups;
});

// Manual slide navigation methods
const nextSlide = () => {
  if (productGroups.value.length <= 1) return;
  activeSlide.value = (activeSlide.value + 1) % productGroups.value.length;
};

const prevSlide = () => {
  if (productGroups.value.length <= 1) return;
  activeSlide.value =
    (activeSlide.value - 1 + productGroups.value.length) % productGroups.value.length;
};

// Mobile scroll functions
const scrollToProduct = (index) => {
  if (!horizontalScroll.value) return;
  
  const productWidth = 300; // Width of each product card + gap
  const scrollPosition = index * productWidth;
  
  horizontalScroll.value.scrollTo({
    left: scrollPosition,
    behavior: 'smooth'
  });
  
  activeMobileSlide.value = index;
};

// Handle scroll event to update active indicator
const handleScroll = () => {
  if (!horizontalScroll.value || !isMobile.value) return;
  
  const scrollLeft = horizontalScroll.value.scrollLeft;
  const productWidth = 300;
  const currentIndex = Math.round(scrollLeft / productWidth);
  
  activeMobileSlide.value = Math.min(currentIndex, props.products.length - 1);
};

// Handle window resize
const handleResize = () => {
  isMobile.value = window.innerWidth < 1024;
};

// Watch for changes in products to reset carousel
watch(() => props.products, () => {
  activeSlide.value = 0;
  activeMobileSlide.value = 0;
});

let carouselInterval = null;

// Auto-rotate carousel (desktop only)
onMounted(() => {
  window.addEventListener('resize', handleResize);
  
  // Add scroll listener for mobile indicators
  if (horizontalScroll.value) {
    horizontalScroll.value.addEventListener('scroll', handleScroll);
  }
  
  if (!isMobile.value) {
    carouselInterval = setInterval(() => {
      if (productGroups.value.length > 1) {
        nextSlide();
      }
    }, 6000);
  }

});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  
  if (horizontalScroll.value) {
    horizontalScroll.value.removeEventListener('scroll', handleScroll);
  }
  
  if (carouselInterval) {
    clearInterval(carouselInterval);
  }
});

// Restart interval when switching from mobile to desktop
watch(isMobile, (newVal) => {
  if (carouselInterval) {
    clearInterval(carouselInterval);
  }
  
  if (!newVal && productGroups.value.length > 1) {
    carouselInterval = setInterval(() => {
      nextSlide();
    }, 6000);
  }
});

</script>

<style scoped>
.products-carousel {
  position: relative;
  margin-bottom: 1rem;
  overflow: visible;
}

.product-reveal-item {
  opacity: 0;
  transform: translate3d(0, 18px, 0);
  animation: carousel-card-reveal 520ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
  animation-delay: var(--reveal-delay, 0ms);
  will-change: opacity, transform;
}

@keyframes carousel-card-reveal {
  from {
    opacity: 0;
    transform: translate3d(0, 18px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

/* Mobile/Tablet horizontal scroll */
.mobile-scroll-container {
  position: relative;
  width: 100%;
  overflow: visible;
}

.horizontal-scroll {
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  overflow-y: visible;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Hide scrollbar for Firefox */
  padding: 0.5rem 0 1rem 0;
  scroll-snap-type: x mandatory;
}

/* Hide scrollbar for webkit browsers */
.horizontal-scroll::-webkit-scrollbar {
  display: none;
}

.product-scroll-item {
  flex: 0 0 300px;
  min-width: 300px;
  max-width: 300px;
  scroll-snap-align: start;
}

/* Arrows */
.carousel-arrows {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 2.5rem;
}

.arrow-btn {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1.5px solid #ccc;
  background: #fff;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, color 0.2s, opacity 0.2s;
  font-size: 0.8rem;
  flex-shrink: 0;
}

.arrow-btn:not(:disabled):hover {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: #fff;
}

.arrow-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* Desktop carousel viewport */
.carousel-viewport {
  overflow: hidden;
  position: relative;
}

.carousel-slide {
  width: 100%;
}

/* Desktop carousel viewport */
.carousel-viewport {
  overflow: hidden;
  position: relative;
  min-height: 10px;
}

.carousel-slide {
  width: 100%;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: opacity 0.78s cubic-bezier(0.22, 1, 0.36, 1);
}

.slide-fade-enter-active {
  position: relative;
}

.slide-fade-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-to,
.slide-fade-leave-from {
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition: none;
  }

  .product-reveal-item {
    animation: none;
    opacity: 1;
    transform: none;
  }
}

/* Desktop carousel legacy (hide old controls) */
.carousel-control-prev,
.carousel-control-next {
  display: none;
}

/* Skeleton loading styles */
.product-skeleton {
  background: #0b0b0b;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  overflow: hidden;
  min-height: 440px;
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.35);
}

.skeleton-img {
  height: 300px;
  background: linear-gradient(90deg, #171717 25%, #252525 50%, #171717 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.skeleton-body {
  padding: 1rem;
}

.skeleton-brand {
  height: 0.8rem;
  width: 42%;
  background: linear-gradient(90deg, #171717 25%, #252525 50%, #171717 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 0.8rem;
}

.skeleton-text {
  height: 20px;
  background: linear-gradient(90deg, #171717 25%, #252525 50%, #171717 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 0.75rem;
}

.skeleton-text.short {
  width: 62%;
}

.skeleton-price {
  height: 38px;
  width: 70%;
  background: linear-gradient(90deg, #171717 25%, #252525 50%, #171717 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-top: 0.45rem;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Responsive adjustments */
@media (max-width: 1023px) {
  .mobile-scroll-container {
    margin: 0;
    width: 100%;
  }
  
  .horizontal-scroll {
    padding: 0.5rem 1rem 1rem 1rem;
    gap: 1rem;
  }
  
  .product-scroll-item {
    flex: 0 0 300px;
    min-width: 300px;
    max-width: 300px;
  }
}

@media (max-width: 767px) {
  .carousel-control-prev,
  .carousel-control-next {
    display: none;
  }
  
  .horizontal-scroll {
    padding: 0.5rem 0.75rem 1rem 0.75rem;
    gap: 0.75rem;
  }
  
  .product-scroll-item {
    flex: 0 0 280px;
    min-width: 280px;
    max-width: 280px;
  }
}

@media (max-width: 480px) {
  .horizontal-scroll {
    padding: 0.5rem 0.5rem 1rem 0.5rem;
    gap: 0.5rem;
  }
  
  .product-scroll-item {
    flex: 0 0 270px;
    min-width: 270px;
    max-width: 270px;
  }
}
</style>
